import React, { useState, FC } from 'react';
import { TProductPriorModelsWithFilterProps } from './types/productPriorModelsWithFilter.types';
import { BreakpointsInPx } from '../../constants/breakpoints.constants';
import DesktopFilter from './components/desktopFilter';
import MobileFilter from './components/mobileFilter';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import useExperienceEditorPlaceholder from '../../hooks/useExperienceEditorPlaceholder';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import { ComponentConsumerProps, isEditorActive, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import { FilterContext, TFilterContextData } from './fiilterContext';
import { ProductPriorModelsWithFilterModel } from './models/productPriorModelsWithFilter.model';
import { ALL_LINES, ALL_MODELS, ALL_SERIES } from '../../constants/seriesFilter.constants';
import NoMatch from '../@shared/filters/components/noMatch';
import ModelCards from './components/modelCards';
import Pagination from '../@shared/pagination';
import useMediaQuery from '../../hooks/useMediaQuery';

const ProductPriorModelsWithFilter: FC<ComponentConsumerProps & TProductPriorModelsWithFilterProps> = (props) => {
  const apiResponse = isEmptyResponse(props) && Number(props?.fields?.apiData?.cards?.length) > 0;
  const isPlaceholderShown = useExperienceEditorPlaceholder(props);
  const isWide = useMediaQuery(`(min-width: ${BreakpointsInPx['tbl-l']})`, "null");
  const [selectedFilters, setSelectedFilters] = useState<TFilterContextData>(() => ({
    // model will not be re-intialized when state data is returned from function
    modelSelectedInd: ALL_MODELS,
    seriesSelectedInd: ALL_SERIES,
    lineSelectedInd: ALL_LINES,
    searchedTerm: '',
    model: new ProductPriorModelsWithFilterModel(props),
  }));
  const [pageNo, setPageNo] = useState(1);
  const filteredCards = selectedFilters.model.filterCards(selectedFilters);
  const noOfColumns = 4;
  const noOfCardsPerPage = noOfColumns * selectedFilters.model.numberOfRows;
  const totalPageCount = Math.ceil(filteredCards.length / noOfCardsPerPage);
  const startIndex = (pageNo - 1) * noOfCardsPerPage;
  const endIndex = pageNo * noOfCardsPerPage;

  const updateFilters = (newFilters: Partial<TFilterContextData> = {}) => {
    setSelectedFilters({ ...selectedFilters, ...newFilters });
    setPageNo(1);
  };

  const resetFilters = () => {
    updateFilters({ lineSelectedInd: ALL_LINES, seriesSelectedInd: ALL_SERIES, modelSelectedInd: ALL_MODELS, searchedTerm: '' });
  };

  // Component is unreleased
  if (props?.fields?.isComponentUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return (
      <ExperienceEditorComponentPlaceholder image={props.fields?.itemData?.thumbnail} componentName={props?.rendering?.componentName} />
    );
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!apiResponse && isEditorActive()) {
    return <EmptyApiDataMessage componentName={props?.rendering?.componentName} />;
  }

  // No data and not in EE
  if (!apiResponse && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  return (
    <>
      <FilterContext.Provider value={{ ...selectedFilters, updateFilters, resetFilters }}>
        {(isWide || isWide === "null") && <DesktopFilter />}
        {(!isWide || isWide === "null") && <MobileFilter />}
        <ModelCards cards={filteredCards?.slice(startIndex, endIndex)} />
      </FilterContext.Provider>
      {filteredCards?.length === 0 && (
        <NoMatch
          titleDictionaryKey="cnhi-models-no-matches-found-title"
          descriptionDictionaryKey="cnhi-models-no-matches-found-description"
        />
      )}
      <Pagination currentPage={pageNo} pages={totalPageCount} goToPage={(selectedPage) => setPageNo(selectedPage)} />
    </>
  );
};

export default withSitecoreContext()(ProductPriorModelsWithFilter);
